import { ApolloError, gql, useMutation } from '@apollo/client';

import { SupergraphsListServiceFragment } from 'src/app/account/supergraphListPage/SupergraphsList';
import { SupergraphListGraphDeferredMetadataFragment } from 'src/app/account/supergraphListPage/SupergraphsListItem';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { ServiceAlreadyExistsRegex } from 'src/lib/graphqlTypes/serverErrors';

export const useCreateSupergraph = () => {
  const [
    createSupergraphMutation,
    {
      data: createSupergraphResponse,
      error: createSupergraphError,
      loading: createSupergraphLoading,
    },
  ] = useMutation<
    GraphQLTypes.CreateSupergraph,
    GraphQLTypes.CreateSupergraphVariables
  >(
    gql`
      mutation CreateSupergraph(
        $accountId: ID!
        $graphType: GraphType!
        $hiddenFromUninvitedNonAdmin: Boolean!
        $graphId: ID!
        $title: String!
        $variantCreationConfig: VariantCreationConfig
      ) {
        account(id: $accountId) {
          createGraph(
            graphType: $graphType
            hiddenFromUninvitedNonAdmin: $hiddenFromUninvitedNonAdmin
            id: $graphId
            title: $title
            variantCreationConfig: $variantCreationConfig
          ) {
            ... on Service {
              id
              variants {
                id
                url
              }
              ...SupergraphsListServiceFragment
              ...SupergraphListGraphDeferredMetadataFragment
            }
            ... on GraphCreationError {
              message
            }
          }
        }
      }
      ${SupergraphsListServiceFragment}
      ${SupergraphListGraphDeferredMetadataFragment}
    `,
    {
      context: appLinkContext({
        ignoreSentryReporting: [
          (graphqlError) =>
            ServiceAlreadyExistsRegex.test(graphqlError.message),
        ],
      }),
      update(cache, { data: resultData }, { variables }) {
        const successfulResult =
          resultData?.account?.createGraph.__typename === 'Service'
            ? resultData.account.createGraph
            : undefined;
        if (!successfulResult) return;

        cache.modify({
          id: cache.identify({
            __typename: 'Account',
            id: variables?.accountId,
          }),
          fields: {
            services: (cachedGraphs = [], { toReference }) => {
              const newServiceRef = toReference({
                __typename: successfulResult.__typename,
                id: successfulResult.id,
              });
              return cachedGraphs.concat(newServiceRef);
            },
          },
        });

        cache.evict({
          id: cache.identify({
            __typename: 'Account',
            id: variables?.accountId,
          }),
          fieldName: 'graphsConnection',
        });
      },
    },
  );

  const createSupergraph = async (
    variables: GraphQLTypes.CreateSupergraphVariables,
  ) => {
    const createSupergraphResult = await createSupergraphMutation({
      variables,
    });

    return createSupergraphResult;
  };

  return {
    createSupergraph,
    createSupergraphResponse,
    createSupergraphError:
      createSupergraphResponse?.account?.createGraph?.__typename ===
      'GraphCreationError'
        ? new ApolloError({
            errorMessage:
              createSupergraphResponse.account.createGraph.message?.includes(
                'Service already exists',
              )
                ? 'A graph with this ID already exists'
                : createSupergraphResponse.account.createGraph.message,
          })
        : createSupergraphError,
    createSupergraphLoading,
  };
};
